import Vue from "vue";
import { divIcon as DivIcon, point as Point } from "leaflet";
import {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LLayerGroup,
    LGeoJson,
} from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import MapCountryPopup from "./MapCountryPopup.vue";

export default {
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        "v-marker-cluster": Vue2LeafletMarkerCluster,
        LLayerGroup,
        LGeoJson,
        MapCountryPopup,
    },
    data() {
        return {
            map: false,
            geoJSONStyle: {
                stroke: true,
                fill: true,
                fillColor: "#ECEFF1",
                fillOpacity: 1,
            },
            geoJSON: null,
            enableTooltip: true,
            fillColor: "#ECEFF1",
            clusterOptions: {
                // Create our custom cluster icon replacement with the `iconCreateFunction` api
                // See: https://github.com/Leaflet/Leaflet.markercluster#customising-the-clustered-markers
                iconCreateFunction: (cluster) => {
                    var childCount = cluster.getChildCount();
                    let c = "map-marker-cluster";
                    if (childCount < 10) {
                        c += "--small";
                    } else if (childCount < 30) {
                        c += "--medium";
                    } else {
                        c += "--large";
                    }

                    return new DivIcon({
                        html: "<div><span>" + childCount + "</span></div>",
                        className: "map-marker-cluster " + c,
                        iconSize: new Point(40, 40),
                    });
                },
                showCoverageOnHover: false,
                removeOutsideVisibleBounds: true,
            },
            itemData: {},
        };
    },
    async created() {
        this.loading = true;
        const response = await this.$http.get(this.mapConfig.GEOJSON_URL);
        const data = await response.data;
        this.geoJSON = data;
        this.loading = false;
    },
    mounted() {
        this.$nextTick(() => {
            this.map = this.$refs.map.mapObject;

            this.addClassToMarkerParents();

            this.map.on("clustermouseover", this.addClassToMarkerParents);
            this.map.on("animationend", this.addClassToMarkerParents);
            this.map.on("zoomend", this.addClassToMarkerParents);
            this.map.on("moveend", this.addClassToMarkerParents);

            this.observeMarkerChanges();
        });
    },
    computed: {
        mapConfig() {
            return this.$store.getters.mapConfig;
        },
        filteredItems() {
            return this.$store.getters.filteredItems;
        },
        filteredItemsCount() {
            return this.$store.getters.filteredItemsCount;
        },
        currentView() {
            return this.$store.getters.currentView;
        },
        currentTopics() {
            if (this.itemData.topics) {
                return this.itemData.topics
                    .map((topic) => topic.label)
                    .join(", ");
            }

            return false;
        },
        projectLink() {
            if (this.itemData.countries) {
                return this.itemData.countries[0].link;
            }

            return false;
        },
        options() {
            return {
                onEachFeature: this.onEachFeatureFunction,
            };
        },
        styleFunction() {
            const fillColor = this.fillColor; // important! need touch fillColor in computed for re-calculate when change fillColor
            return () => {
                return {
                    weight: 1,
                    color: "#cdcdcd",
                    opacity: 0,
                    fillColor: fillColor,
                    fillOpacity: 0,
                };
            };
        },
        onEachFeatureFunction() {
            if (!this.enableTooltip) {
                return () => {};
            }
            const filteredItems = this.$store.getters.filteredItems;
            return (feature, layer) => {
                if (
                    filteredItems.find((item) =>
                        item.countries.some(
                            (country) =>
                                country.code === feature.properties.adm0_a3
                        )
                    )
                ) {
                    layer.setStyle({
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0.7,
                    });

                    // get country profile link
                    const projectCountries = filteredItems.find((item) =>
                        item.countries.some(
                            (country) =>
                                country.code === feature.properties.adm0_a3
                        )
                    )?.countries;

                    let countryProfileLink = "";
                    let countryName = "";

                    if (projectCountries) {
                        countryProfileLink =
                            projectCountries.find(
                                (item) =>
                                    item.code === feature.properties.adm0_a3
                            ).link || null;
                        countryName = projectCountries.find(
                            (item) => item.code === feature.properties.adm0_a3
                        ).label;
                    }

                    const countryPopup = new Vue(
                        Object.assign({}, MapCountryPopup, {
                            propsData: {
                                countryName: countryName,
                                countryProfileLink: countryProfileLink,
                            },
                        })
                    ).$mount();

                    layer.bindPopup(countryPopup.$el);
                }
            };
        },
    },
    watch: {
        filteredItems() {
            this.map.panTo(this.$store.getters.mapConfig.CENTER);
        },
    },
    methods: {
        openListView(args) {
            this.$store.dispatch("resetFilters");
            this.$store.dispatch("setCurrentView", "list");
            this.$store.dispatch("updateFilters", args);
        },
        openMarkerPopup(id, coords) {
            this.itemData = this.$store.getters.itemByID(id);
            this.$refs.markerPopup.mapObject.openPopup(coords);
            this.map.panTo(coords);
        },
        formatStatus(status) {
            if (!status) return;

            return status[0].id === 2
                ? this.$t.message("statusCompleted")
                : this.$t.message("statusOngoing");
        },
        getIcon() {
            return new DivIcon({
                className: "leaflet-marker-icon--single",
                html: `<span></span>`,
            });
        },
        addClassToMarkerParents() {
            this.$nextTick(() => {
                if(this.$refs && this.$refs.map && this.$refs.map.$el) {
                    const markerElements = this.$refs.map.$el.querySelectorAll(".leaflet-marker-icon");
                    markerElements.forEach((el) => {
                        el.setAttribute("aria-hidden", "true");
                        el.setAttribute("focusable", "false");
                        el.setAttribute("tabindex", "-1");
                    });
                }
            });
        },
        observeMarkerChanges() {
            // Target the element where markers are added
            const targetNode = this.$refs.map.$el;

            // Options for the observer (which mutations to observe)
            const config = { childList: true, subtree: true };

            // Callback function to execute when mutations are observed
            const callback = (mutationsList) => {
                for (let mutation of mutationsList) {
                    if (mutation.type === "childList") {
                        this.addClassToMarkerParents();
                    }
                }
            };

            // Create an observer instance linked to the callback function
            this.observer = new MutationObserver(callback);

            // Start observing the target node for configured mutations
            this.observer.observe(targetNode, config);
        },
    },
};
